/* import { Filter, X } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { DatePickerWithPresets } from "@/shared/components/range-picker/RangePicker";

interface OrdersFiltersProps {
  totalCount: number;
  filteredCount: number;
}

export function OrdersFilters({
  totalCount,
  filteredCount,
}: OrdersFiltersProps) {
  return (
    <div className="bg-white rounded-lg border p-4 mb-6">
      <div className="flex items-center gap-3 mb-4">
        <Filter className="w-5 h-5 text-gray-500" />

        <DatePickerWithPresets />

        <Select>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Маршрут" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="almaty">Алматы</SelectItem>
            <SelectItem value="nursultan">Нур-Султан</SelectItem>
            <SelectItem value="shymkent">Шымкент</SelectItem>
          </SelectContent>
        </Select>

        <Select>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Авиакомпания" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="scat">SCAT Airlines</SelectItem>
            <SelectItem value="astana">Air Astana</SelectItem>
            <SelectItem value="flyarystan">FlyArystan</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="ghost" size="sm">
          Сброс фильтров
        </Button>
        <span className="text-sm text-gray-600 ml-auto">
          Показано {filteredCount} из {totalCount}
        </span>
      </div>
    </div>
  );
}
 */

import { Filter } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { DatePickerWithPresets } from "@/shared/components/range-picker/RangePicker";
import { DynamicSelectFilter } from "@/shared/components/filters/Filters";
import type { Airport, SystemDictionary } from "@/shared/api/filters";
import { useState, type Dispatch, type SetStateAction } from "react";
import type { OrdersPageFiltersState } from "../../pages/OrdersPage";

// Пример ваших моковых данных
const MOCK_DATA = {
  airlines: ["SCAT Airlines", "Air Astana", "FlyArystan"],
  employee_roles: [
    { code: "admin", label: "Администратор" },
    { code: "manager", label: "Менеджер" },
  ],
  baggage_presets: [
    { carryOnKg: 8, checkedKg: 23, label: "Стандарт (23кг + 8кг)" },
    { carryOnKg: 5, checkedKg: 20, label: "Эконом (20кг + 5кг)" },
  ],
};

interface OrdersFiltersProps {
  totalCount: number;
  filteredCount: number;
  dictionary: SystemDictionary | undefined;
  setFilters: Dispatch<SetStateAction<OrdersPageFiltersState>>;
  filters: OrdersPageFiltersState;
  airports:Airport[]
}

export function OrdersFilters({
  totalCount,
  filteredCount,
  dictionary,
  setFilters,
  filters,
  airports,
}: OrdersFiltersProps) {
  // Функция для обновления конкретного фильтра
  const updateFilter = (key: string, value: string) => {
    // Если выбрали "all", сбрасываем фильтр (передаем пустую строку)
    setFilters((prev) => ({ ...prev, [key]: value === "all" ? "" : value }));
  };

  const resetFilters = () => {
    setFilters({
      airline: "",
      status: "",
      page: 1,
      paymentStatus: "",
      pageSize: 20,
      dateFrom: "",
      dateTo: "",
      toIata: "",
    fromIata: "",
    });
  };

  const handleDateChange = (dateFrom: string, dateTo: string) => {
    setFilters((prev) => ({ ...prev, dateFrom, dateTo, page: 1 }));
  };

  if (!dictionary) return null;

  return (
    <div className="bg-white rounded-lg border p-4 mb-6">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Filter className="w-5 h-5 text-gray-500" />

        <DatePickerWithPresets
          dateFrom={filters.dateFrom}
          dateTo={filters.dateTo}
          onChange={handleDateChange}
        />

        {/* 1. Пример с массивом простых строк (airlines) */}
        <DynamicSelectFilter
          placeholder="Авиакомпания"
          data={dictionary.airlines}
          value={filters.airline || undefined}
          onChange={(val) => updateFilter("airline", val)}
          // Для простых строк value и label одинаковые
          valueExtractor={(item) => item}
          labelExtractor={(item) => item}
        />

        <DynamicSelectFilter
          placeholder="Статус"
          data={dictionary.order_statuses}
          value={filters.status || undefined}
          onChange={(val) => updateFilter("status", val)}
          valueExtractor={(item) => item.code}
          labelExtractor={(item) => item.label}
        />
        <DynamicSelectFilter
          placeholder="Статус оплаты"
          data={dictionary.payment_statuses}
          value={filters.paymentStatus || undefined}
          onChange={(val) => updateFilter("paymentStatus", val)}
          valueExtractor={(item) => item.code}
          labelExtractor={(item) => item.label}
        />
        <DynamicSelectFilter
          placeholder="Вылет"
          searchPlaceholder="Поиск по аэропорту..."
          data={airports}
          value={filters.fromIata || undefined}
          onChange={(val) => updateFilter("fromIata", val)}
          valueExtractor={(item) => item.IATA}
          labelExtractor={(item) => `${item.IATA} — ${item.City.ru}`}
        />
        <DynamicSelectFilter
          placeholder="Прилет"
          searchPlaceholder="Поиск по аэропорту..."
          data={airports}
          value={filters.toIata || undefined}
          onChange={(val) => updateFilter("toIata", val)}
          valueExtractor={(item) => item.IATA}
          labelExtractor={(item) => `${item.IATA} — ${item.City.ru}`}
        />
        <Button
          variant="ghost"
          size="sm"
          className="h-10"
          onClick={resetFilters}
        >
          Сброс фильтров
        </Button>
        <span className="text-sm text-gray-600 ml-auto">
          Показано {filteredCount} из {totalCount}
        </span>
      </div>
    </div>
  );
}
